"use client";

import React, { useState } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import { GroqImage } from "@/helpers/Image";
import { PortableTextBlock } from "sanity";
import { GROQImage } from "@/sanity/lib/definitions";
import cardBg02 from "@/public/aboutUs/cardBg01.png";
import cardBg01 from "@/public/aboutUs/cardBg02.png";
import { Dialog, DialogContent, DialogTitle } from "@/shadcn/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";

interface PerformanceCardProps {
  card: {
    text: PortableTextBlock[];
    images: GROQImage[];
  };
  index: number;
}

export default function PerformanceCard({ card, index }: PerformanceCardProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? (card.images?.length || 1) - 1 : prev - 1,
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === (card.images?.length || 1) - 1 ? 0 : prev + 1,
    );
  };

  const backgroundImage = index % 2 === 0 ? cardBg02 : cardBg01;
  const aspectRatio = index % 2 === 0 ? "aspect-[274/161]" : "aspect-[223/158]";
  const imageBgColor = index === 2 ? "bg-white/60" : "bg-white";

  // Determine flex direction for desktop (odd = reverse)
  const flexDirection = index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse";

  return (
    <div
      className={`flex flex-col ${flexDirection} gap-[3rem] lg:gap-[7rem] w-full bg-white py-[2.12rem] lg:py-[2.75rem] px-[1.88rem] lg:px-[2.38rem] rounded-[3rem]`}
    >
      {/* Rich Text Section */}
      {card.text && (
        <div className="flex flex-col flex-1 justify-between pb-0 lg:gap-0 gap-[3rem] px-[3rem] pt-[2rem] lg:px-[4rem] lg:py-[3rem]">
          <PortableText
            value={card.text}
            components={customRichTextComponent({
              block: {
                h3: ({ children }) => (
                  <h3 className="mb-0 capitalize font-instrument-sans font-[600] text-[var(--prose-heading-color)] text-[2.5rem] leading-[3rem] lg:text-[3.5rem] lg:leading-[3.6rem]">
                    {children}
                  </h3>
                ),
                normal: ({ children }) => (
                  <p className="mb-0 text-[#103037]/70 leading-[3rem] lg:leading-[2.275rem]">
                    {children}
                  </p>
                ),
              },
            })}
          />
        </div>
      )}

      {/* Carousel Section */}
      {card.images && card.images.length > 0 && (
        <>
          <div
            onClick={() => setIsDialogOpen(true)}
            className="flex overflow-hidden relative flex-col flex-1 items-center cursor-pointer gap-[1.17rem] py-[3rem] px-[4.25rem] rounded-[1.00838rem] bg-[#A8B8A5] max-w-[44.625rem] lg:rounded-[1.125rem]"
          >
            {/* Background Image */}
            <Image
              src={backgroundImage}
              alt="Background"
              className="object-cover absolute inset-0 z-0 w-full h-full"
              fill
            />

            <div
              className={`${imageBgColor} p-[.88rem] rounded-[1.02925rem] relative z-10`}
            >
              <div
                className={`w-full ${aspectRatio} rounded-[0.58388rem] overflow-hidden relative`}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentImageIndex}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="w-full h-full"
                  >
                    <GroqImage
                      className="object-fill w-full h-full"
                      alt={`Performance image ${currentImageIndex + 1}`}
                      src={card.images[currentImageIndex]}
                    />
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {card.images.length > 1 && (
              <div className="flex relative z-10 flex-row justify-between items-center w-full gap-[1rem]">
                {/* Image indicators */}
                <div className="flex items-center gap-[0.5rem]">
                  {card.images.map((_, idx) => (
                    <motion.div
                      key={idx}
                      animate={{
                        width:
                          idx === currentImageIndex ? "6.81125rem" : "2rem",
                        backgroundColor:
                          idx === currentImageIndex
                            ? "rgba(255, 255, 255, 1)"
                            : "rgba(255, 255, 255, 0.5)",
                      }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="rounded-full h-[.35rem]"
                    />
                  ))}
                </div>

                <div className="flex items-center gap-[.78rem]">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePrevImage();
                    }}
                    disabled={currentImageIndex === 0}
                    className={`w-[4rem] h-[4rem] flex items-center justify-center rounded-full transition-colors ${
                      currentImageIndex === 0
                        ? "bg-transparent border border-white cursor-not-allowed"
                        : "bg-white hover:bg-white/50"
                    }`}
                    aria-label="Previous image"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="11"
                      height="11"
                      viewBox="0 0 11 11"
                      fill="none"
                    >
                      <path
                        d="M3.08939 5.55429L6.41396 8.87886C6.44485 8.90975 6.48152 8.93425 6.52187 8.95096C6.56223 8.96768 6.60549 8.97629 6.64917 8.97629C6.69285 8.97629 6.73611 8.96768 6.77647 8.95096C6.81682 8.93425 6.85349 8.90975 6.88438 8.87886C6.91527 8.84797 6.93977 8.8113 6.95649 8.77094C6.97321 8.73058 6.98181 8.68733 6.98181 8.64364C6.98181 8.59996 6.97321 8.5567 6.95649 8.51635C6.93977 8.47599 6.91527 8.43932 6.88438 8.40843L3.79461 5.31908L6.88438 2.22972C6.94677 2.16734 6.98181 2.08273 6.98181 1.99451C6.98181 1.90628 6.94677 1.82168 6.88438 1.75929C6.822 1.69691 6.73739 1.66187 6.64917 1.66187C6.56095 1.66187 6.47634 1.69691 6.41396 1.75929L3.08939 5.08386C3.05848 5.11474 3.03396 5.1514 3.01723 5.19176C3.0005 5.23212 2.99188 5.27539 2.99188 5.31908C2.99188 5.36277 3.0005 5.40603 3.01723 5.44639C3.03396 5.48675 3.05848 5.52341 3.08939 5.55429Z"
                        fill={currentImageIndex === 0 ? "white" : "black"}
                      />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNextImage();
                    }}
                    disabled={currentImageIndex === card.images.length - 1}
                    className={`w-[4rem] h-[4rem] flex items-center justify-center rounded-full transition-colors ${
                      currentImageIndex === card.images.length - 1
                        ? "bg-transparent border border-white cursor-not-allowed"
                        : "bg-white hover:bg-white/50"
                    }`}
                    aria-label="Next image"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="11"
                      height="11"
                      viewBox="0 0 11 11"
                      fill="none"
                    >
                      <path
                        d="M7.5494 5.55429L4.22484 8.87886C4.19395 8.90975 4.15728 8.93425 4.11692 8.95096C4.07656 8.96768 4.03331 8.97629 3.98962 8.97629C3.94594 8.97629 3.90269 8.96768 3.86233 8.95096C3.82197 8.93425 3.7853 8.90975 3.75441 8.87886C3.72352 8.84797 3.69902 8.8113 3.6823 8.77094C3.66559 8.73058 3.65698 8.68733 3.65698 8.64364C3.65698 8.59996 3.66559 8.5567 3.6823 8.51635C3.69902 8.47599 3.72352 8.43932 3.75441 8.40843L6.84418 5.31908L3.75441 2.22972C3.69203 2.16734 3.65698 2.08273 3.65698 1.99451C3.65698 1.90628 3.69203 1.82168 3.75441 1.75929C3.81679 1.69691 3.9014 1.66187 3.98962 1.66187C4.07785 1.66187 4.16245 1.69691 4.22484 1.75929L7.5494 5.08386C7.58032 5.11474 7.60484 5.1514 7.62157 5.19176C7.6383 5.23212 7.64691 5.27539 7.64691 5.31908C7.64691 5.36277 7.6383 5.40603 7.62157 5.44639C7.60484 5.48675 7.58032 5.52341 7.5494 5.55429Z"
                        fill={
                          currentImageIndex === card.images.length - 1
                            ? "white"
                            : "black"
                        }
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Dialog with enlarged carousel */}
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogContent className="p-0 bg-transparent border-none max-w-[90vw] lg:max-w-[65vw]">
              <VisuallyHidden.Root>
                <DialogTitle>Performance Image Gallery</DialogTitle>
              </VisuallyHidden.Root>
              <div className="flex overflow-hidden flex-col items-center border shadow-lg transition-all gap-[2rem] py-[4rem] px-[3rem] rounded-[1.5rem] bg-[#A8B8A5] border-white/30 lg:py-[6rem] lg:px-[6rem]">
                <div
                  className={`${imageBgColor} p-[1rem] lg:p-[1.5rem] rounded-[1.5rem] max-w-full w-full h-[75vh]`}
                >
                  <div className="overflow-hidden relative w-full h-full rounded-[1rem] min-w-[35rem] lg:min-w-[75rem]">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={currentImageIndex}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="w-full h-full"
                      >
                        <GroqImage
                          className=" !w-full !h-full object-fill"
                          alt={`Performance image ${currentImageIndex + 1}`}
                          src={card.images[currentImageIndex]}
                        />
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>

                {card.images.length > 1 && (
                  <div className="flex flex-row justify-between items-center w-full gap-[1rem]">
                    {/* Image indicators */}
                    <div className="flex items-center gap-[0.75rem]">
                      {card.images.map((_, idx) => (
                        <motion.div
                          key={idx}
                          animate={{
                            width:
                              idx === currentImageIndex ? "8rem" : "2.5rem",
                            backgroundColor:
                              idx === currentImageIndex
                                ? "rgba(255, 255, 255, 1)"
                                : "rgba(255, 255, 255, 0.5)",
                          }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          className="rounded-full h-[.5rem]"
                        />
                      ))}
                    </div>

                    <div className="flex items-center gap-[1rem]">
                      <button
                        onClick={handlePrevImage}
                        disabled={currentImageIndex === 0}
                        className={`w-[5rem] h-[5rem] flex items-center justify-center rounded-full transition-colors ${
                          currentImageIndex === 0
                            ? "bg-transparent border border-white cursor-not-allowed"
                            : "bg-white hover:bg-white/50"
                        }`}
                        aria-label="Previous image"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 11 11"
                          fill="none"
                        >
                          <path
                            d="M3.08939 5.55429L6.41396 8.87886C6.44485 8.90975 6.48152 8.93425 6.52187 8.95096C6.56223 8.96768 6.60549 8.97629 6.64917 8.97629C6.69285 8.97629 6.73611 8.96768 6.77647 8.95096C6.81682 8.93425 6.85349 8.90975 6.88438 8.87886C6.91527 8.84797 6.93977 8.8113 6.95649 8.77094C6.97321 8.73058 6.98181 8.68733 6.98181 8.64364C6.98181 8.59996 6.97321 8.5567 6.95649 8.51635C6.93977 8.47599 6.91527 8.43932 6.88438 8.40843L3.79461 5.31908L6.88438 2.22972C6.94677 2.16734 6.98181 2.08273 6.98181 1.99451C6.98181 1.90628 6.94677 1.82168 6.88438 1.75929C6.822 1.69691 6.73739 1.66187 6.64917 1.66187C6.56095 1.66187 6.47634 1.69691 6.41396 1.75929L3.08939 5.08386C3.05848 5.11474 3.03396 5.1514 3.01723 5.19176C3.0005 5.23212 2.99188 5.27539 2.99188 5.31908C2.99188 5.36277 3.0005 5.40603 3.01723 5.44639C3.03396 5.48675 3.05848 5.52341 3.08939 5.55429Z"
                            fill={currentImageIndex === 0 ? "white" : "black"}
                          />
                        </svg>
                      </button>
                      <button
                        onClick={handleNextImage}
                        disabled={currentImageIndex === card.images.length - 1}
                        className={`w-[5rem] h-[5rem] flex items-center justify-center rounded-full transition-colors ${
                          currentImageIndex === card.images.length - 1
                            ? "bg-transparent border border-white cursor-not-allowed"
                            : "bg-white hover:bg-white/50"
                        }`}
                        aria-label="Next image"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 11 11"
                          fill="none"
                        >
                          <path
                            d="M7.5494 5.55429L4.22484 8.87886C4.19395 8.90975 4.15728 8.93425 4.11692 8.95096C4.07656 8.96768 4.03331 8.97629 3.98962 8.97629C3.94594 8.97629 3.90269 8.96768 3.86233 8.95096C3.82197 8.93425 3.7853 8.90975 3.75441 8.87886C3.72352 8.84797 3.69902 8.8113 3.6823 8.77094C3.66559 8.73058 3.65698 8.68733 3.65698 8.64364C3.65698 8.59996 3.66559 8.5567 3.6823 8.51635C3.69902 8.47599 3.72352 8.43932 3.75441 8.40843L6.84418 5.31908L3.75441 2.22972C3.69203 2.16734 3.65698 2.08273 3.65698 1.99451C3.65698 1.90628 3.69203 1.82168 3.75441 1.75929C3.81679 1.69691 3.9014 1.66187 3.98962 1.66187C4.07785 1.66187 4.16245 1.69691 4.22484 1.75929L7.5494 5.08386C7.58032 5.11474 7.60484 5.1514 7.62157 5.19176C7.6383 5.23212 7.64691 5.27539 7.64691 5.31908C7.64691 5.36277 7.6383 5.40603 7.62157 5.44639C7.60484 5.48675 7.58032 5.52341 7.5494 5.55429Z"
                            fill={
                              currentImageIndex === card.images.length - 1
                                ? "white"
                                : "black"
                            }
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}
